import { useState, useEffect, useRef, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import SignaturePad from 'signature_pad';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import IconBtn from 'components/common/IconBtn';
import Contract from './Contract';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as ExpandIcon } from 'assets/icons/expand.svg';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { HiExclamationCircle } from 'react-icons/hi';
import api from 'api';
import { RootState, AppDispatch } from 'redux/store';
import { CANDIDATE_ROLES } from 'data/constants/roles';
import { setAlert } from 'redux/alert';
import { fetchRetainerAgreement } from 'redux/retainer-agreement/agreement.middleware';
import {
    getClientStampedSigning,
    isAgreementSigningAvailable,
    toClientSignatureHtml,
} from 'utils/agreement';
import { getErrorMessage } from 'utils/v2/errorHelper';

import styles from './AgreementSignature.module.scss';

const OutlinedBtn = styled(Button)({
    border: '2px solid #4A52BB',
    textTransform: 'none',
    padding: '12px 18px',
    height: 'content-fit',
    color: '#282828',
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '17px',
    '&:hover': {
        border: '2px solid #F3A712',
        backgroundColor: '#f8f8f8',
    },
    '&:active': {
        boxShadow: 'none',
    },
    '&:focus': {
        boxShadow: '0 0 0 0.2rem rgba(243, 167, 18,.5)',
    },
    '.Mui-disabled': {
        backgroundColor: '#F5F5F5 !important',
        border: '2px solid #F5F5F5',
    },
});

type SignatureElement = {
    beginStroke: { x: number; y: number; startTime: number };
    endStroke: { x: number; y: number; endTime: number };
    straightLineLength: number;
    differenceInTime: number;
};

type Props = {
    appID: string | null;
    onFullscreenMode: (value: boolean) => void;
    onUserSignedAgreement: () => void;
};

const AgreementSignature = ({ appID, onFullscreenMode, onUserSignedAgreement }: Props) => {
    const [fullscreenMode, setFullscreenMode] = useState<boolean>(false);
    const [signedAgreement, setSignedAgreement] = useState<Blob | null>(null);

    const [isOpenSignaturePanel, setOpenSignaturePanel] = useState<boolean>(false);
    const [signature, setSignature] = useState<string | null>(null);
    const [isApplySignatureDisabled, setApplySignatureDisabled] = useState<boolean>(true);
    const [signatureElements, setSignatureElements] = useState<SignatureElement[]>([]);
    const signatureWindowRef = useRef<HTMLDivElement | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    // Payload already submitted (stamped PDF or drawn signature), so re-renders don't re-post it
    const uploadedPayloadRef = useRef<Blob | string | null>(null);

    const { file, summary, isLoading, error } = useSelector(
        (state: RootState) => state.retainerAgreement
    );
    const isAgent = useSelector(
        (state: RootState) => !CANDIDATE_ROLES.includes(state.user.entity.role)
    );

    const isAgreementSigned = !!summary?.signed;
    const canSignAgreement = isAgreementSigningAvailable(summary);
    // Template retainers: the backend embeds the signature and renders the PDF itself
    const isServerEmbedded = summary?.signing?.method === 'server_embedded';
    const signaturePosition = getClientStampedSigning(summary);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const signaturePad = useRef<SignaturePad | null>(null);
    const pdfViewerRef = useRef<HTMLDivElement | null>(null);

    const dispatch = useDispatch<AppDispatch>();

    const validateSignature = useCallback(() => {
        if (isOpenSignaturePanel) {
            const longSignatureElements = signatureElements.filter((el) => {
                return el.straightLineLength > 20;
            });

            if (longSignatureElements.length > 0) {
                setApplySignatureDisabled(false);
                return;
            }
            if (longSignatureElements.length === 0) {
                const closedLoops = signatureElements.filter(
                    (el) => el.straightLineLength < 10 && el.differenceInTime > 800
                );
                if (closedLoops.length >= 2) setApplySignatureDisabled(false);
            }
        }
    }, [isOpenSignaturePanel, signatureElements]);

    const uploadSignedRetainerAgreement = useCallback(async () => {
        if (!appID || isUploading || !canSignAgreement) return;

        const formData: FormData = new FormData();
        if (isServerEmbedded) {
            if (!signature) return;
            formData.append('client_signature', toClientSignatureHtml(signature));
        } else {
            if (!signedAgreement) return;
            formData.append('retainer_agreement', signedAgreement);
            // Also stored on the agreement row (not used for rendering)
            if (signature) {
                formData.append('client_signature', toClientSignatureHtml(signature));
            }
        }

        setIsUploading(true);
        try {
            await api.agreement.post(appID, formData);
        } catch (error: unknown) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            // 409 means agreement already exists — treat as already signed
            if (status !== 409) {
                setIsUploading(false);
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, 'Failed to sign agreement'),
                        type: 'error',
                    })
                );
                return;
            }
        }

        // Re-fetch so `summary.signed` and the stored (server-rendered) signed PDF are shown
        setSignature(null);
        await dispatch(fetchRetainerAgreement(appID));
        setIsUploading(false);
        onUserSignedAgreement();
    }, [
        appID,
        canSignAgreement,
        dispatch,
        isServerEmbedded,
        isUploading,
        onUserSignedAgreement,
        signature,
        signedAgreement,
    ]);

    useEffect(() => {
        window.addEventListener('resize', resizeCanvas);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);

    useEffect(() => {
        if (appID) dispatch(fetchRetainerAgreement(appID));
    }, [appID, dispatch]);

    useEffect(() => {
        if (canvasRef.current) {
            signaturePad.current = new SignaturePad(canvasRef.current);
            const windowWidth = window.innerWidth;
            if (windowWidth < 768) {
                canvasRef.current.width = 360;
                canvasRef.current.height = 340;
            } else if (windowWidth < 1024) {
                canvasRef.current.width = 540;
                canvasRef.current.height = 480;
            } else {
                canvasRef.current.width = 820;
                canvasRef.current.height = 410;
            }
        }

        signaturePad.current?.addEventListener('beginStroke', (e) => handleBeginStroke(e));
        signaturePad.current?.addEventListener('endStroke', (e) => handleEndStroke(e));

        return () => {
            signaturePad.current?.removeEventListener('beginStroke', (e) => handleBeginStroke(e));
            signaturePad.current?.addEventListener('endStroke', (e) => handleEndStroke(e));
        };
    }, [isOpenSignaturePanel]);

    useEffect(() => {
        validateSignature();
    }, [validateSignature]);

    useEffect(() => {
        onFullscreenMode(fullscreenMode);
    }, [fullscreenMode, onFullscreenMode]);

    useEffect(() => {
        if (signedAgreement) {
            setTimeout(() => {
                scrollToBottom();
            }, 1000);
        }
    }, [signedAgreement]);

    // Upload as soon as the client produced its payload: the pdf-lib stamped PDF, or
    // only the drawn signature for server-embedded retainers
    useEffect(() => {
        const payload = isServerEmbedded ? signature : signedAgreement;
        if (!payload || uploadedPayloadRef.current === payload) return;
        uploadedPayloadRef.current = payload;
        void uploadSignedRetainerAgreement();
    }, [isServerEmbedded, signature, signedAgreement, uploadSignedRetainerAgreement]);

    const downloadSignedAgreement = () => {
        const blob = signedAgreement ?? file;
        if (!blob) return;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'retainer_agreement.pdf';
        link.click();
    };

    const toggleFullscreenMode = () => {
        setFullscreenMode((state) => !state);
    };

    const scrollToBottom = () => {
        if (pdfViewerRef.current) {
            const scrollHeight = pdfViewerRef.current.scrollHeight;
            const height = pdfViewerRef.current.clientHeight;
            const maxScrollTop = scrollHeight - height;
            pdfViewerRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    };

    //** SIGNATURE PANEL **/
    const openCloseSignaturePanel = () => {
        setOpenSignaturePanel((state) => !state);
    };

    const saveSignature = () => {
        if (signaturePad.current) {
            const signatureData = signaturePad.current.toDataURL();

            setSignature(signatureData);
            openCloseSignaturePanel();
            setSignatureElements([]);
            setApplySignatureDisabled(true);
        }
    };

    const clearSignature = () => {
        if (signaturePad.current) {
            signaturePad.current.clear();
            setApplySignatureDisabled(true);
            setSignatureElements([]);
        }
    };

    const handleBeginStroke = (event: Event) => {
        setSignatureElements((prevState) => {
            return [...prevState].concat({
                beginStroke: {
                    x: (event as CustomEvent).detail.x,
                    y: (event as CustomEvent).detail.y,
                    startTime: (event as CustomEvent).detail.timeStamp,
                },
                endStroke: { x: 0, y: 0, endTime: 0 },
                straightLineLength: 0,
                differenceInTime: 0,
            });
        });
    };

    const handleEndStroke = (event: Event) => {
        setSignatureElements((prevState) => {
            const lastElement = prevState[prevState.length - 1];
            const differenceInTime =
                (event as CustomEvent).detail.timeStamp - lastElement.beginStroke.startTime;
            const endStrokeX = (event as CustomEvent).detail.x;
            const endStrokeY = (event as CustomEvent).detail.y;
            const hypotenuseLength = Math.sqrt(
                (lastElement.beginStroke.x - endStrokeX) ** 2 +
                    (lastElement.beginStroke.y - endStrokeY) ** 2
            );
            lastElement.endStroke.x = endStrokeX;
            lastElement.endStroke.y = endStrokeY;
            lastElement.endStroke.endTime = (event as CustomEvent).detail.timeStamp;
            lastElement.differenceInTime = differenceInTime;
            lastElement.straightLineLength = hypotenuseLength;
            return [...prevState];
        });
    };

    function resizeCanvas() {
        if (signatureWindowRef.current && canvasRef.current) {
            canvasRef.current.width = signatureWindowRef.current.offsetWidth * 0.8;
            canvasRef.current.height = signatureWindowRef.current.offsetHeight * 0.8;

            setSignatureElements([]);
            setApplySignatureDisabled(true);
        }
    }

    return (
        <>
            <div className={styles['document-preview']}>
                <div className={styles['side-padding']}>
                    <section className={styles['document-preview__header']}>
                        <h3 className={styles['document-preview__title']}>
                            {'Retainer Agreement'}
                        </h3>
                        <div className={styles.dashboard}>
                            <IconBtn
                                variant="primary"
                                onClick={downloadSignedAgreement}
                                disabled={!isAgreementSigned || !!error}
                            >
                                <DownloadIcon
                                    fill={!isAgreementSigned || !!error ? '#C0C0C0' : '#282828'}
                                />
                            </IconBtn>
                            <IconBtn variant="primary" onClick={toggleFullscreenMode}>
                                <ExpandIcon fill="#282828" />
                            </IconBtn>
                        </div>
                    </section>
                </div>

                {error && !isLoading && (
                    <div className={styles.error}>
                        <HiExclamationCircle className={styles['error__icon']} />
                        <h3 className={styles['error__text']}>Oops!</h3>
                        <span className={styles['error__desc']}>Something went wrong.</span>
                    </div>
                )}

                {isLoading && (
                    <div className={styles.loader}>
                        <CircularProgress color="primary" />
                    </div>
                )}

                {!isOpenSignaturePanel && !isLoading && !error && (
                    <div className={styles['pdf-viewer__wrapper']}>
                        <div ref={pdfViewerRef} className={styles['pdf-viewer']}>
                            <Contract
                                file={file}
                                signature={signature}
                                signaturePosition={signaturePosition}
                                onSignedAgreement={(blob) => setSignedAgreement(blob)}
                            />
                        </div>
                    </div>
                )}
                {isOpenSignaturePanel && !isLoading && !error && (
                    <div className={styles['signature-pad-panel']} ref={signatureWindowRef}>
                        <div className={styles.panel}>
                            <canvas ref={canvasRef} />
                        </div>
                        <div className={styles['button-icon-wrapper']}>
                            <IconBtn
                                className={styles['back-button']}
                                variant="primary"
                                onClick={openCloseSignaturePanel}
                            >
                                <ArrowLeftIcon fill="inherit" height={12} />
                            </IconBtn>
                        </div>
                    </div>
                )}

                {canSignAgreement && !error && !isAgent && (
                    <div className={styles['btn-wrapper']}>
                        {!isOpenSignaturePanel && !signedAgreement && (
                            <button
                                className={styles['btn-filled']}
                                type="button"
                                disabled={isUploading}
                                onClick={openCloseSignaturePanel}
                            >
                                Sign Agreement
                            </button>
                        )}
                        {!isOpenSignaturePanel && signedAgreement && !error && (
                            <>
                                <OutlinedBtn onClick={openCloseSignaturePanel} variant="outlined">
                                    Re-sign Agreement
                                </OutlinedBtn>
                                <button
                                    className={styles['btn-filled']}
                                    type="button"
                                    disabled={isUploading}
                                    onClick={uploadSignedRetainerAgreement}
                                >
                                    Upload Agreement
                                </button>
                            </>
                        )}
                        {isOpenSignaturePanel && (
                            <>
                                <OutlinedBtn onClick={clearSignature} variant="outlined">
                                    Clear Signature
                                </OutlinedBtn>
                                <button
                                    className={styles['btn-filled']}
                                    type="button"
                                    disabled={isApplySignatureDisabled}
                                    onClick={saveSignature}
                                >
                                    Apply Signature
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default AgreementSignature;
